<template>
    <div class="product-detail__bottom mt-0 mt-md-5 w-100">
        <div id="js-rating-list">
            <h2 class="h3 mt-3 mt-0">{{ $t('productDetail.rating.title') }} {{ productName }}</h2>
            <div class="conteiner">
                <div class="row justify-content-md-between align-items-center">
                    <div class="col-12 col-md-8">
                        <ul class="tabsList">
                            <li class="tabsList__item">
                                <a
                                    class="tabsList__link"
                                    href="#vue-product-rating-list"
                                    :class="{ 'tabsList__link--active': showTypeComment === 'all' }"
                                    data-cy="all-ratings"
                                    @click.prevent="showType('all')"
                                    >{{ $t('comments.all') }} ({{ totalCount }})</a
                                >
                            </li>
                            <li class="tabsList__item">
                                <a
                                    class="tabsList__link"
                                    href="#vue-product-rating-list"
                                    :class="{ 'tabsList__link--active': showTypeComment === 'verified' }"
                                    data-cy="verified-ratings"
                                    @click.prevent="showType('verified')"
                                    v-html="getCountVerifiedComments()"
                                ></a>
                            </li>
                        </ul>
                    </div>
                    <div v-if="comments.length > 0" class="mt-3 mt-md-0 col-12 col-md-4 mb-3 mb-md-0 order-3">
                        <div class="input-group input-group--rating">
                            <div class="input-group-text border-0 pr-0 bg-grey-light2">
                                <img width="15" :src="`/nuxt-assets/images/icons/magnifier.svg`" class="d-block" />
                            </div>
                            <input
                                v-model="searchQuery"
                                class="form-control form-control-sm border-0 bg-grey-light2"
                                type="text"
                                :placeholder="$t('comments.search')"
                                @keyup="search()"
                            />
                            <div v-if="searchQueries.length" class="input-group-text border-0 bg-grey-light2">
                                <button class="border-0 p-0 bg-grey-light2 cursor-point" @click="resetSearch">
                                    <img width="15" :src="`/nuxt-assets/images/icons/close.svg`" class="d-block" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div id="vue-product-rating-list">
                <RatingComment
                    v-for="(comment, commentIndex) in comments"
                    :key="commentIndex"
                    :comment="comment"
                    :search-queries="searchQueries"
                    @set-loading="setLoading"
                />

                <div v-if="searchQueries.length" class="text-center mt-3">
                    <p>
                        {{ $t('productDetail.rating.noItems') }}
                    </p>
                    <button class="btn btn-primary m-auto text-white" @click="resetSearch">
                        {{ $t('productDetail.rating.showAll') }}
                    </button>
                </div>

                <div v-if="totalCount > limit + offset && !searchQueries.length" class="text-center mt-3">
                    <button class="btn btn-primary m-auto text-white" @click="showMore">
                        {{ $t('productDetail.rating.rateShowAll') }}
                    </button>
                </div>
            </div>
        </div>
        <Loader v-if="loading" />
        <Modal
            modal-name="ask-question-login"
            role="dialog"
            aria-hidden="true"
            :title="$t('rating.title')"
            @hidden="modalRatingVisible = false"
        >
            <!-- FIXME redirect -->
            <div class="modal-body" v-html="$t('rating.login.mustLogin')"></div>
        </Modal>
    </div>
</template>

<script lang="ts">
import Loader from '~/components/ui/Loader.vue'
import Modal from '~/components/Modal.vue'
import PilulkaTab from '~/components/elements/PilulkaTab.vue'
import PilulkaTabs from '~/components/elements/PilulkaTabs.vue'
import RatingComment from '~/components/layout/productDetail/RatingComment.vue'
import Post from '~/models/Discussion/Post'

export default defineNuxtComponent({
    components: {
        RatingComment,
        Loader,
        Modal,
        PilulkaTab,
        PilulkaTabs,
    },
    props: {
        objectId: {
            type: Number,
            required: true,
        },
        type: {
            type: Number,
            required: true,
        },
        productName: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            comments: [],
            totalCount: 0,
            verifiedPurchaseCount: 0,
            limit: 20,
            offset: 0,
            isUsefulMessage: '',
            showIsUsefulMessage: false,
            loading: false,
            searchQuery: '',
            searchTotalResults: -1,
            posts: [],
            searchQueries: [],
            showTypeComment: 'all',
            modalRatingVisible: true,
        }
    },

    async mounted() {
        await this.fetch()
    },

    methods: {
        async fetch() {
            const response = await this.$repository.getComments({
                objectId: this.objectId,
                type: this.type,
                offset: this.offset,
                limit: this.limit,
                isVerifiedPurchase: this.showTypeComment === 'verified',
            })
            this.comments = response.reviews
            this.totalCount = response.total
            this.verifiedPurchaseCount = response.verifiedPurchaseCount
        },

        async showMore(event: Event) {
            event.stopPropagation()
            this.offset += this.limit
            const response = await this.$repository.getComments({
                objectId: this.objectId,
                type: this.type,
                offset: this.offset,
                limit: this.limit,
                isVerifiedPurchase: this.showTypeComment === 'verified',
            })
            this.comments = this.comments.concat(response.reviews)
            this.totalCount = response.total
            this.verifiedPurchaseCount = response.verifiedPurchaseCount
        },

        resetSearch() {
            this.searchQuery = ''
            this.search()
        },

        setLoading(value: boolean) {
            this.loading = value
        },

        isTextMatchingQuery(text: string, query: string) {
            return text.toLowerCase().normalize('NFC').includes(query)
        },

        search() {
            const searchQuery = this.searchQuery.toLowerCase().normalize('NFC')

            this.searchTotalResults = this.posts.reduce((total: number, question: Post) => {
                if (!question.isPublished) {
                    return false
                }

                if (searchQuery === '') {
                    question.isShown = true
                    question.isAnswerListHidden = this.productId === null

                    return -1
                }

                const postMatch =
                    this.isTextMatchingQuery(question.text, searchQuery) ||
                    this.isTextMatchingQuery(question.title, searchQuery) ||
                    this.isTextMatchingQuery(this.productName, searchQuery)

                const matchingAnswers = question.answers.filter((answer) => {
                    if (!answer.isPublished) {
                        return false
                    }

                    return this.isTextMatchingQuery(answer.text, searchQuery)
                })

                const isMatch = postMatch || matchingAnswers.length > 0
                question.isShown = isMatch
                question.isAnswerListHidden = matchingAnswers.length === 0

                return total + isMatch
            }, 0)

            this.searchQueries = searchQuery.split(/ /g).filter((query: string) => query)
        },

        async showType(type: 'all' | 'verified') {
            this.showTypeComment = type
            await this.fetch()
        },

        getCountVerifiedComments() {
            return this.$t('comments.verified') + ' (' + this.verifiedPurchaseCount + ')'
        },
    },
})
</script>
<style lang="scss" scoped>
.btn-simple {
    border: 1px solid transparent;
    border-radius: 10px;
    color: #333;
    font-weight: 400;
    background: transparent;
    cursor: pointer;
}

.top-0 {
    top: 0;
}
.left-0 {
    left: 0;
}
.bg-search-term {
    background-color: rgba(194, 164, 209, 0.35);
}
.tooltip-usefulmessage {
    background: #fff;
    border-radius: 5px;
    padding: 0.5rem 1rem;
    box-shadow: 0 0 10px #cecece;
    position: absolute;
    z-index: 10;
    width: 250px;
    margin-left: -125px;
    left: 50%;
    margin-bottom: 10px;
    bottom: 100%;
    white-space: normal;
    text-align: center;
}

.product-detail__rating-content {
    width: 100%;
}

.product-detail__rating-content .btn {
    font-weight: 400;
    color: $color-dark;
    border-radius: 10px;
    border: 1px solid transparent;
}

.product-detail__rating-content .btn:hover {
    background-color: $color-grey;
    border: 1px solid $color-grey-extra-dark;
}

.product-detail__rating-box {
    border-bottom: 1px solid rgb(225 225 225);
    flex-wrap: wrap;

    @media (min-width: 768px) {
        display: flex;
    }
}

@media (min-width: 480px) {
    .product-detail__rating-box {
        flex-wrap: nowrap;
    }
}

@media (min-width: 768px) {
    .product-detail__rating-name {
        width: 200px;
    }

    .product-detail__rating-box--activeSearch {
        display: none;
    }

    .product-detail__rating-box--activeSearch-activeItem {
        display: flex;
    }
}

.product-detail__rating-img {
    margin-right: 8px;
    flex: 0 0 13px;
}

.product-detail__rating-star {
    height: 17px;
}

.product-detail__rating-star .ico {
    margin: 0;
}

.nav-link--link {
    font-weight: 500;
    cursor: pointer;
}

.ratingRecommend {
    border-radius: 50%;
    background-color: $color-green-light;
    flex: 0 0 26px;
    width: 26px;
    height: 26px;
    text-align: center;
    margin-right: 6px;
    padding: 2px 0 0 2px;
}

.input-group--rating {
    border-radius: 20px;
    overflow: hidden;
}

.tabsList {
    display: inline-flex;
    list-style: none;
    padding: 0;
    margin: 0;
}

.tabsList__item {
    overflow: hidden;

    &:first-child {
        border-top-left-radius: $border-radius;
        border-bottom-left-radius: $border-radius;
    }

    &:last-child {
        border-top-right-radius: $border-radius;
        border-bottom-right-radius: $border-radius;
    }
}

.tabsList__link {
    display: inline-block;
    padding: 0 0.875rem;
    font-size: 0.875rem;
    line-height: 2.5rem;
    background-color: $color-grey;
    cursor: pointer;
    color: $color-dark;
    font-weight: 600;

    &:hover {
        @media (min-width: 1024px) {
            background-color: $color-grey-dark;
        }
    }

    &--active {
        background-color: $color-cta-extra-light;
    }
}

.ratingProcess {
    text-decoration: underline;
    font-size: 0.75rem;
    line-height: 1.75rem;
    color: $color-dark;

    &:hover {
        @media (min-width: 890px) {
            text-decoration: none;
        }
    }
}
.btn-simple--yes:hover {
    background: $color-green-light;
}
.btn-simple--no:hover {
    background: $color-orange-light;
}
</style>
